import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { AiFillEye, AiFillGithub } from 'react-icons/ai';
import { motion, AnimatePresence } from 'framer-motion';
import allProjectsData from './ProjectsData';
import { setAllProjectsData } from '../../redux/actions/actionCreate';

// ─── Animation variants ────────────────────────────────────────────────────
const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
  exit: { opacity: 0, y: -16, transition: { duration: 0.2 } },
};

// ─── Tag pill ──────────────────────────────────────────────────────────────
const TagPill = ({ label }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase bg-white/10 text-white/60 border border-white/10">
    {label}
  </span>
);

TagPill.propTypes = {
  label: PropTypes.string.isRequired,
};

// ─── Project card ──────────────────────────────────────────────────────────
const ProjectCard = ({ project, index }) => {
  const tags = Object.values(project.tags || {}).filter(Boolean);

  return (
    <motion.article
      custom={index}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      layout
      className="group relative flex flex-col rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/25 transition-colors duration-300"
    >
      {/* ── Image + hover overlay ── */}
      <div className="relative w-full aspect-[16/9] overflow-hidden">
        <img
          src={project.image}
          alt={project.title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />

        {/* Dark scrim — always slightly visible, stronger on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        {/* Action links — slide up on hover */}
        <div className="absolute inset-0 flex items-center justify-center gap-4 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0 bg-black/50 backdrop-blur-[2px]">
          {project.liveLink && (
            <a
              href={project.liveLink}
              target="_blank"
              rel="noreferrer"
              aria-label="Live preview"
              className="flex items-center justify-center w-12 h-12 rounded-full bg-white/10 border border-white/30 text-white hover:bg-rose-500 hover:border-rose-500 transition-all duration-200 hover:scale-110"
            >
              <AiFillEye className="w-5 h-5" />
            </a>
          )}
          {project.codeLink && (
            <a
              href={project.codeLink}
              target="_blank"
              rel="noreferrer"
              aria-label="GitHub repository"
              className="flex items-center justify-center w-12 h-12 rounded-full bg-white/10 border border-white/30 text-white hover:bg-white hover:text-black transition-all duration-200 hover:scale-110"
            >
              <AiFillGithub className="w-5 h-5" />
            </a>
          )}
        </div>

        {/* Category badge */}
        {project.category && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-black/60 text-white/80 border border-white/15 backdrop-blur-sm">
            {project.category}
          </span>
        )}
      </div>

      {/* ── Content ── */}
      <div className="flex flex-col gap-2 p-5 flex-1">
        <h3 className="text-sm font-bold tracking-wide text-white/90 uppercase leading-tight">
          {project.title}
        </h3>

        {project.description && (
          <p className="text-xs text-white/50 leading-relaxed line-clamp-3 flex-1">
            {project.description}
          </p>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2 pt-3 border-t border-white/10">
            {tags.map((tag) => (
              <TagPill key={tag} label={tag} />
            ))}
          </div>
        )}

        {/* Always-visible links — the hover overlay never shows on touch screens */}
        <div className="flex items-center gap-4 mt-3 text-[11px] font-semibold uppercase tracking-widest">
          {project.liveLink && (
            <a href={project.liveLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-rose-300/90 hover:text-rose-200 transition-colors">
              <AiFillEye className="w-3.5 h-3.5" />
              Live demo
            </a>
          )}
          {project.codeLink && (
            <a href={project.codeLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white transition-colors">
              <AiFillGithub className="w-3.5 h-3.5" />
              Source
            </a>
          )}
        </div>
      </div>
    </motion.article>
  );
};

ProjectCard.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    image: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    liveLink: PropTypes.string,
    codeLink: PropTypes.string,
    category: PropTypes.string,
    tags: PropTypes.objectOf(PropTypes.string),
  }).isRequired,
  index: PropTypes.number.isRequired,
};

// ─── Filter button ─────────────────────────────────────────────────────────
const FilterBtn = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      relative px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest
      transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400
      ${active
      ? 'bg-rose-500 text-white shadow-lg shadow-rose-500/30'
      : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10 hover:text-white/80'}
    `}
  >
    {label}
  </button>
);

FilterBtn.propTypes = {
  label: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

// ─── Main component ────────────────────────────────────────────────────────
const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('ALL');
  const dispatch = useDispatch();
  const projects = useSelector((state) => state.projects);
  const containerRef = useRef(null);

  useEffect(() => {
    dispatch(setAllProjectsData(allProjectsData));
  }, [dispatch]);

  // ── Filtering logic (this was broken before — now fixed) ──
  const FILTERS = ['ALL', 'FRONTEND', 'FULL-STACK'];

  const filtered = activeFilter === 'ALL'
    ? projects
    : projects.filter(
      (p) => p.category?.toUpperCase() === activeFilter,
    );

  return (
    <div
      ref={containerRef}
      // Fill the snap-scroll viewport exactly — no margin-top hacks
      className="relative w-full h-screen overflow-y-auto flex flex-col"
      style={{
        background: 'radial-gradient(ellipse at 20% 50%, rgba(234,92,0,0.07) 0%, transparent 60%), #0a0907',
      }}
    >
      {/* ── Subtle grain texture overlay ── */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03] z-0"
        style={{
          backgroundImage: 'url(\'data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E\')',
        }}
      />

      <div className="relative z-10 flex flex-col flex-1 w-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-20">

        {/* ── Header ── */}
        <div className="mb-10 sm:mb-14">
          <p className="text-[10px] tracking-[0.3em] text-white/30 uppercase mb-3">
            Portfolio
          </p>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-white">
              Selected
              {' '}
              <span className="italic text-rose-400">projects</span>
            </h2>

            {/* ── Filter pills ── */}
            <div className="flex items-center gap-2 flex-wrap">
              {FILTERS.map((f) => (
                <FilterBtn
                  key={f}
                  label={f}
                  active={activeFilter === f}
                  onClick={() => setActiveFilter(f)}
                />
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="mt-6 h-px w-full bg-gradient-to-r from-rose-500/40 via-white/10 to-transparent" />
        </div>

        {/* ── Project count ── */}
        <p className="text-xs text-white/30 tracking-widest uppercase mb-6">
          {filtered.length}
          {' '}
          project
          {filtered.length !== 1 ? 's' : ''}
          {activeFilter !== 'ALL' && ` · ${activeFilter}`}
        </p>

        {/* ── Grid ── */}
        <AnimatePresence mode="popLayout">
          {filtered.length > 0 ? (
            <motion.div
              key="grid"
              layout
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
            >
              {filtered.map((project, i) => (
                <ProjectCard key={project.id} project={project} index={i} />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center flex-1 py-24 gap-4"
            >
              <div className="w-16 h-16 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center">
                <span className="text-white/20 text-2xl">∅</span>
              </div>
              <p className="text-white/30 text-sm tracking-widest uppercase">
                {`No ${activeFilter.toLowerCase()} projects yet`}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Bottom breathing room for scroll ── */}
        <div className="h-16 shrink-0" />
      </div>
    </div>
  );
};

export default Projects;
